import React, { useEffect, useState } from 'react';
import { Loader2, ArrowRight, ChevronDown, ChevronRight, History } from 'lucide-react';
import { Card, CardContent } from '@components/Card';
import { salesOrderService } from '../../services/salesOrderService';
import { OrderChangeEntry, OrderFieldChange } from '../../types/salesOrder';

// Rótulos pt-BR dos tipos de alteração gravados na auditoria (ChangeType do backend).
const CHANGE_TYPE_LABELS: Record<string, string> = {
  created: 'Pedido criado',
  order_update: 'Edição do pedido',
  status_change: 'Troca de status',
  address_update: 'Alteração de endereço',
  customer_update: 'Alteração de cliente',
  item_add: 'Item adicionado',
  item_update: 'Item alterado',
  item_delete: 'Item removido',
  installment_update: 'Alteração de parcela',
  installments_recalculated: 'Parcelas recalculadas',
  internal_observation: 'Observação interna',
  followup: 'Follow-up',
  mark_delivered: 'Marcado como entregue',
  reverse_stock: 'Estorno de estoque',
  card_refund: 'Estorno de cartão',
  company_change: 'Troca de empresa',
  address_review: 'Análise de endereço (antifraude)',
  recalculation: 'Recálculo forçado',
  material_receipt_return: 'Devolução (recebimento)',
};

const changeTypeLabel = (t?: string) => (t ? CHANGE_TYPE_LABELS[t] ?? t : 'Alteração');

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleString('pt-BR');
};

const kindStyle = (kind: OrderFieldChange['kind']) => {
  switch (kind) {
    case 'added':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'removed':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-amber-50 text-amber-700 border-amber-200';
  }
};

const StatusPill: React.FC<{ label?: string; id?: number }> = ({ label, id }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
    {label || (id != null ? `Status ${id}` : '—')}
  </span>
);

// Renderiza uma única diferença campo-a-campo (de → para) conforme o tipo (added/removed/modified).
const FieldChangeRow: React.FC<{ change: OrderFieldChange }> = ({ change }) => (
  <li className="flex flex-wrap items-center gap-2 py-1 text-sm">
    <span className="font-medium text-slate-700">{change.label || change.field}:</span>
    {change.kind !== 'added' && (
      <span className="line-through text-slate-400 break-all">{change.old || '∅'}</span>
    )}
    {change.kind === 'modified' && <ArrowRight size={13} className="text-slate-400 shrink-0" />}
    {change.kind !== 'removed' && (
      <span className="text-slate-900 break-all">{change.new || '∅'}</span>
    )}
    <span className={`ml-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${kindStyle(change.kind)}`}>
      {change.kind === 'added' ? 'incluído' : change.kind === 'removed' ? 'removido' : 'alterado'}
    </span>
  </li>
);

// Um item da timeline: cabeçalho (tipo/quem/quando), transição de status e diff de campos,
// com o RawJson bruto num bloco expansível de fallback.
const HistoryEntryCard: React.FC<{ entry: OrderChangeEntry }> = ({ entry }) => {
  const [rawOpen, setRawOpen] = useState(false);
  const isStatusChange =
    entry.change_type === 'status_change' ||
    entry.old_order_status_id != null ||
    entry.old_order_status != null;

  return (
    <div className="relative pl-6">
      {/* marcador da timeline */}
      <span className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full bg-blue-500 ring-4 ring-blue-100" />
      <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-50 text-blue-700">
            {changeTypeLabel(entry.change_type)}
          </span>
          <span className="text-xs text-slate-500">{formatDateTime(entry.change_timestamp)}</span>
        </div>

        <div className="mt-1 text-xs text-slate-500">
          por <span className="font-medium text-slate-700">{entry.changed_by || 'sistema'}</span>
        </div>

        {isStatusChange && (
          <div className="mt-2 flex items-center gap-2">
            <StatusPill label={entry.old_order_status} id={entry.old_order_status_id} />
            <ArrowRight size={14} className="text-slate-400" />
            <StatusPill label={entry.order_status} id={entry.order_status_id} />
          </div>
        )}

        {entry.changes && entry.changes.length > 0 && (
          <ul className="mt-2 divide-y divide-slate-100">
            {entry.changes.map((c, i) => (
              <FieldChangeRow key={`${c.field}-${i}`} change={c} />
            ))}
          </ul>
        )}

        {(!entry.changes || entry.changes.length === 0) && !isStatusChange && (
          <p className="mt-2 text-sm text-slate-400">Sem diferenças de campo registradas.</p>
        )}

        {entry.raw_json && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setRawOpen(v => !v)}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
              {rawOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              Payload bruto (JSON)
            </button>
            {rawOpen && (
              <pre className="mt-1 max-h-64 overflow-auto rounded bg-slate-900 p-3 text-[11px] leading-snug text-slate-100">
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(entry.raw_json), null, 2);
                  } catch {
                    return entry.raw_json;
                  }
                })()}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Aba "Histórico" da tela de detalhes do pedido. Carrega a trilha de auditoria sob demanda
 * (ao montar, ou seja, quando a aba é aberta). Acesso restrito a admin/finance é garantido
 * no backend (permissão orders.view_history) e pela renderização condicional na view.
 */
export const OrderHistoryTab: React.FC<{ orderId: number }> = ({ orderId }) => {
  const [entries, setEntries] = useState<OrderChangeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    salesOrderService
      .getOrderHistory(orderId)
      .then(data => {
        if (active) setEntries(data);
      })
      .catch(err => {
        if (active) setError(err instanceof Error ? err.message : 'Falha ao carregar o histórico');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [orderId]);

  return (
    <Card>
      <CardContent className="p-4">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-8 text-slate-500">
            <Loader2 size={18} className="animate-spin" /> Carregando histórico…
          </div>
        )}

        {!loading && error && (
          <div className="py-6 text-center text-sm text-red-600">{error}</div>
        )}

        {!loading && !error && entries.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-8 text-slate-400">
            <History size={28} />
            <span className="text-sm">Nenhuma alteração registrada para este pedido.</span>
          </div>
        )}

        {!loading && !error && entries.length > 0 && (
          <div className="relative space-y-4 before:absolute before:left-[4px] before:top-1 before:bottom-1 before:w-px before:bg-slate-200">
            {entries.map(entry => (
              <HistoryEntryCard key={entry.order_change_request_id} entry={entry} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderHistoryTab;
